import { useAtom } from 'jotai';
import { hoveredInfoAreaAtom, isMobile } from '../states/planner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAnchor } from './CustomShapeArea';

export const AreaInfoHover = () => {
  const [hoveredArea] = useAtom(hoveredInfoAreaAtom);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 }); // in %, already scaled
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState<boolean>(false);

  const debugPoints = false;
  const scale = isMobile() ? 0.75 : 1;

  const updateSize = useCallback(() => {
    if (tooltipRef.current?.parentElement) {
      const parent = tooltipRef.current.parentElement;
      const { offsetWidth, offsetHeight } = tooltipRef.current;

      // offsetWidth/Height ignore CSS transforms, so multiply by `scale`
      setTooltipSize({
        width: (offsetWidth / parent.offsetWidth) * 100 * scale,
        height: (offsetHeight / parent.offsetHeight) * 100 * scale,
      });
    }
  }, [scale]);

  useEffect(() => {
    if (hoveredArea) {
      requestAnimationFrame(() => setAnimate(true));
    } else {
      setAnimate(false);
    }
  }, [hoveredArea]);

  useEffect(() => {
    if (!tooltipRef.current?.parentElement) return;
    const parent = tooltipRef.current.parentElement;

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(parent);

    return () => observer.disconnect();
  }, [hoveredArea, updateSize]);

  if (!hoveredArea) return null;
  const svgArea = hoveredArea;

  const tooltipplace = svgArea.tooltipplace;
  const anchor = getAnchor(tooltipplace, svgArea, tooltipSize);

  return (
    <>
      <div
        ref={tooltipRef}
        className={`pointer-events-none absolute rounded-md bg-white pb-2 pr-2 pt-2 shadow-lg transition-all duration-300 ease-out ${
          animate ? 'opacity-95' : 'opacity-0'
        }`}
        style={{
          top: `${anchor.y}%`,
          left: `${anchor.x}%`,
          // scale the whole tooltip from its top-left corner so the top/left anchor stays correct
          transform: `scale(${scale * (animate ? 1 : 0.75)})`,
          transformOrigin: 'top left',
          willChange: 'transform, opacity',
        }}
      >
        {tooltipplace === 'top' && (
          <div className="absolute left-1/2 top-full -translate-x-1/2 border-x-[7px] border-t-[7px] border-x-transparent border-t-white opacity-95" />
        )}
        {tooltipplace === 'bottom' && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-x-[7px] border-b-[7px] border-x-transparent border-b-white opacity-95" />
        )}
        {tooltipplace === 'left' && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 border-y-[7px] border-l-[7px] border-y-transparent border-l-white opacity-95" />
        )}
        {tooltipplace === 'right' && (
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-y-[7px] border-r-[7px] border-y-transparent border-r-white opacity-95" />
        )}

        {/* Keep your natural layout; visual size is controlled by `scale` */}
        <div className="max-w-64">
          <div className="pb-1 pl-4 pr-2 pt-1 text-left text-xs font-bold max-[400px]:text-[10px] sm:text-base">
            {hoveredArea.title}
          </div>
          {hoveredArea.text && (
            <div className="w-64 pb-2 pl-4 pr-2 text-left text-xs leading-tight max-[400px]:text-[10px] sm:text-xs">
              {hoveredArea.text}
            </div>
          )}
        </div>
      </div>

      {debugPoints && (
        <svg className="z-2 pointer-events-none absolute inset-0 h-full w-full">
          <circle cx={`${svgArea.left.x}%`} cy={`${svgArea.left.y}%`} r="10" fill="red" />
          <circle cx={`${svgArea.right.x}%`} cy={`${svgArea.right.y}%`} r="10" fill="red" />
          <circle cx={`${svgArea.top.x}%`} cy={`${svgArea.top.y}%`} r="10" fill="red" />
          <circle cx={`${svgArea.bottom.x}%`} cy={`${svgArea.bottom.y}%`} r="10" fill="red" />
        </svg>
      )}
    </>
  );
};
